import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Mail, Phone } from "lucide-react";
import api from "@/lib/api";

const SOCIALS = [
  { name: "linkedin", href: "https://www.linkedin.com/company/glade-physicality-score/", Icon: Linkedin, label: "LinkedIn" },
  { name: "instagram", href: "https://www.instagram.com/physicality.score/", Icon: Instagram, label: "Instagram" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  const [contact, setContact] = useState({ contact_email: "", contact_phone: "" });
  useEffect(() => {
    api.get("/settings").then((r) => setContact(r.data || {})).catch(() => {});
  }, []);

  return (
    <footer
      className="mt-24 border-t border-white/10 px-6 md:px-12 pt-14 pb-8"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10">
        {/* Brand block */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_gps-arena-1/artifacts/itknedvj_GPS%20Logo%20AI-09.png"
              alt="GPS"
              className="h-10 w-10 object-contain logo-glow"
              draggable={false}
            />
            <div className="gps-heading tracking-[0.3em] text-lg">GPS</div>
          </div>
          <p className="text-mute text-xs mt-4 leading-relaxed max-w-sm">
            General Physicality Score. A universal language for human physical capability —
            measured, calibrated, ranked at planetary scale.
          </p>
        </div>

        {/* Nav columns */}
        <div className="md:col-span-2">
          <div className="gps-label">SYSTEM</div>
          <ul className="text-mute text-sm space-y-2 mt-2">
            <li><Link to="/about" className="hover:text-white" data-testid="footer-about">About</Link></li>
            <li><Link to="/tests" className="hover:text-white" data-testid="footer-tests">10 Tests</Link></li>
            <li><Link to="/archetypes" className="hover:text-white" data-testid="footer-archetypes">Archetypes</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="gps-label">ARENA</div>
          <ul className="text-mute text-sm space-y-2 mt-2">
            <li><Link to="/leaderboard" className="hover:text-white" data-testid="footer-leaderboard">Leaderboard</Link></li>
            <li><Link to="/record-holders" className="hover:text-white" data-testid="footer-records">Record Holders</Link></li>
            <li><Link to="/events" className="hover:text-white" data-testid="footer-events">Events</Link></li>
          </ul>
        </div>

        {/* Contact us */}
        <div className="md:col-span-4" data-testid="footer-contact">
          <div className="gps-label">CONTACT US</div>
          <div className="text-mute text-sm space-y-2 mt-2">
            {contact.contact_email && (
              <a
                href={`mailto:${contact.contact_email}`}
                className="flex items-center gap-2 hover:text-white"
                data-testid="footer-contact-email"
              >
                <Mail size={14} className="text-cyan-400" />
                <span className="break-all">{contact.contact_email}</span>
              </a>
            )}
            {contact.contact_phone && (
              <a
                href={`tel:${contact.contact_phone}`}
                className="flex items-center gap-2 hover:text-white"
                data-testid="footer-contact-phone"
              >
                <Phone size={14} className="text-cyan-400" />
                <span>{contact.contact_phone}</span>
              </a>
            )}
          </div>
          <div className="gps-mono text-cyan text-xs flex items-center gap-2 mt-4">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> SYSTEM ONLINE
          </div>
          <div className="text-mute text-[10px] mt-2 gps-mono tracking-[0.2em]">
            v0.1 — CALCULATION RELEASE
          </div>
        </div>
      </div>

      <div className="hud-divider mt-12" />

      <div className="max-w-7xl mx-auto mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-mute text-[10px] gps-mono tracking-[0.25em]" data-testid="footer-copyright">
          COPYRIGHT © GLADE {year}
        </div>
        <div className="text-mute text-[10px] gps-mono tracking-[0.25em] text-center" data-testid="footer-credit">
          DESIGNED AND BUILT BY THE AWESOME PEOPLE OF GLADE{" "}
          <span className="inline-block ml-1 align-middle text-white">◆</span>
        </div>
        <div className="flex items-center gap-3" data-testid="footer-socials">
          {SOCIALS.map(({ name, href, Icon, label }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              data-testid={`footer-social-${name}`}
              className="text-mute hover:text-white transition-colors"
            >
              <Icon size={16} />
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}
